import { Injectable, inject } from "@angular/core";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IMapComponent } from "../map.models";
import { IMapGeometry, IMapGeometryWicket } from "../map.settings";
import { TerraformerService } from "./terraformer.service";
import type { UpdateOptions } from "@arcgis/core/widgets/Sketch/types";

@Injectable({
    providedIn: "root"
})
export class MapSketch2Service {
    private readonly terraformerService = inject(TerraformerService);

    private view: any;
    private sketchViewModel: any;
    private map: IMapComponent;
    private readonly editExtentOptions: UpdateOptions = {
        tool: "transform",
        enableRotation: false,
        enableScaling: true,
        multipleSelectionEnabled: false,
        preserveAspectRatio: true,
        toggleToolOnClick: false
    };

    init(view: any, map: IMapComponent) {
        this.view = view;
        this.map = map;
        this.createSketch();
    }

    edit(layerid: string, geometry: IMapGeometry, callback?: (geometry: IMapGeometry, aborted: boolean, info: any) => void) {
        if (ObjectUtils.isNullOrUndefined(geometry) || ObjectUtils.isNullOrUndefined(layerid)) {
            console.error(`Missing geometry or layer`);
            return;
        }
        const arcLayer = this.view.layerViews.find(l => l.layer.id === layerid).layer;
        const arcGraphic = arcLayer.graphics.find(x => x.getAttribute("id") === geometry.id);
        if (ObjectUtils.isNullOrUndefined(arcGraphic)) {
            console.error(`Graphic not found: ${geometry.id}`);
            return;
        }
        if (!this.sketchViewModel) {
            this.createSketch();
        } else {
            if (this.sketchViewModel.state === "active") {
                this.sketchViewModel.cancel();
            }
        }
        this.sketchViewModel.layer = arcLayer;
        if (callback) {
            const updateEvent = this.sketchViewModel.on("update", (event) => {
                if (event.state === "complete") {
                    this.updateWkt(geometry as IMapGeometryWicket, event.graphics[0]);
                    callback(geometry, event.aborted, event.toolEventInfo);
                    updateEvent.remove();
                }
            });
        }

        if (this.sketchViewModel.state === "ready") {
            window.requestAnimationFrame(() => {
                this.sketchViewModel.update([arcGraphic], this.editExtentOptions).then(() => {
                    // Exekvera promise
                });
            });
        } else {
            this.sketchViewModel.update([arcGraphic], this.editExtentOptions).then(() => {
                // Exekvera promise
            });
        }
    }

    complete(target?: IMapGeometryWicket): IMapGeometryWicket {
        if (ObjectUtils.hasValue(target) && this.sketchViewModel.updateGraphics?.items?.length > 0) {
            this.updateWkt(target, this.sketchViewModel.updateGraphics?.items[0]);
        }
        this.sketchViewModel.complete();
        return target;
    }

    cancel() {
        this.sketchViewModel.cancel();
    }

    private createSketch(arcLayer = null) {
        this.sketchViewModel = new SketchViewModel({
            view: this.view,
            defaultUpdateOptions: this.editExtentOptions
        });
        this.sketchViewModel.updateOnGraphicClick = false;
        if (arcLayer) {
            this.sketchViewModel.layer = arcLayer;
        }
    }

    private updateWkt(geometry: IMapGeometryWicket, arcgisGraphic: any) {
        const wkt = this.terraformerService.arcGisToWkt(arcgisGraphic.geometry);
        geometry.wkt = wkt;
    }

    private destroySketch() {
        this.sketchViewModel.destroy();
        this.sketchViewModel = null;
    }
}
